// Navigation configuration centralized for all roles
// CREATED: Centralized navigation configuration for consistent routing across roles
// Defines navigation items for sidebar, bottom nav, and "More" menu
// UPDATED: Renamed "Mes tâches" to "Accueil" with Home icon for managers and operators
// UPDATED: Removed "Signaler" from navigation - incident reporting is now integrated into Incidents page
// UPDATED: Added "Analytique" navigation item for managers, directors and admins (KPI system v1)

import { Users, Building2, Briefcase, FileText, CircleUser as UserCircle, AlertTriangle, CalendarDays, MoreHorizontal, Home, BarChart3 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface NavigationItem {
  path: string;
  label: string;
  icon: LucideIcon;
  roles: string[];
}

// All available navigation items
export const allNavigationItems: NavigationItem[] = [
  {
    path: '/users',
    label: 'Utilisateurs',
    icon: Users,
    roles: ['admin', 'director']
  },
  {
    path: '/sites',
    label: 'Sites',
    icon: Building2,
    roles: ['admin', 'director']
  },
  {
    path: '/services',
    label: 'Services',
    icon: Briefcase,
    roles: ['admin', 'director']
  },
  {
    path: '/templates',
    label: 'Missions',
    icon: FileText,
    roles: ['admin', 'director']
  },
  {
    path: '/team',
    label: 'Équipe',
    icon: CalendarDays,
    roles: ['admin', 'director', 'manager']
  },
  {
    path: '/incidents',
    label: 'Incidents',
    icon: AlertTriangle,
    roles: ['admin', 'director', 'manager', 'operator']
  },
  {
    path: '/analytics',
    label: 'Statistiques',
    icon: BarChart3,
    roles: ['admin', 'director', 'manager']
  },
  {
    path: '/profile',
    label: 'Profil',
    icon: UserCircle,
    roles: ['admin', 'director', 'manager', 'operator']
  }
];

// Items shown in the "More" menu for Admin/Director on mobile
export const moreMenuItems: NavigationItem[] = [
  {
    path: '/users',
    label: 'Utilisateurs',
    icon: Users,
    roles: ['admin', 'director']
  },
  {
    path: '/sites',
    label: 'Sites',
    icon: Building2,
    roles: ['admin', 'director']
  },
  {
    path: '/services',
    label: 'Services',
    icon: Briefcase,
    roles: ['admin', 'director']
  },
  {
    path: '/templates',
    label: 'Missions',
    icon: FileText,
    roles: ['admin', 'director']
  }
];

// Bottom navigation items per role (mobile only)
export const getBottomNavItems = (role: string): NavigationItem[] => {
  switch (role) {
    case 'admin':
    case 'director':
      return [
        {
          path: '/tasks',
          label: 'Accueil',
          icon: Home,
          roles: ['admin', 'director']
        },
        {
          path: '/team',
          label: 'Équipe',
          icon: CalendarDays,
          roles: ['admin', 'director']
        },
        {
          path: '/incidents',
          label: 'Incidents',
          icon: AlertTriangle,
          roles: ['admin', 'director']
        },
        {
          path: '/profile',
          label: 'Profil',
          icon: UserCircle,
          roles: ['admin', 'director']
        },
        {
          path: '/more',
          label: 'Plus',
          icon: MoreHorizontal,
          roles: ['admin', 'director']
        }
      ];
    case 'manager':
      return [
        {
          path: '/',
          label: 'Accueil',
          icon: Home,
          roles: ['manager']
        },
        {
          path: '/team',
          label: 'Équipe',
          icon: CalendarDays,
          roles: ['manager']
        },
        {
          path: '/analytics',
          label: 'Statistiques',
          icon: BarChart3,
          roles: ['manager']
        },
        {
          path: '/incidents',
          label: 'Incidents',
          icon: AlertTriangle,
          roles: ['manager']
        },
        {
          path: '/profile',
          label: 'Profil',
          icon: UserCircle,
          roles: ['manager']
        }
      ];
    case 'operator':
      return [
        {
          path: '/',
          label: 'Accueil',
          icon: Home,
          roles: ['operator']
        },
        {
          path: '/incidents',
          label: 'Incidents',
          icon: AlertTriangle,
          roles: ['operator']
        },
        {
          path: '/profile',
          label: 'Profil',
          icon: UserCircle,
          roles: ['operator']
        }
      ];
    default:
      return [];
  }
};

// Desktop sidebar items per role
export const getSidebarItems = (role: string): NavigationItem[] => {
  switch (role) {
    case 'admin':
    case 'director':
      return [
        {
          path: '/tasks',
          label: 'Accueil',
          icon: Home,
          roles: ['admin', 'director']
        },
        {
          path: '/team',
          label: 'Équipe',
          icon: CalendarDays,
          roles: ['admin', 'director']
        },
        {
          path: '/users',
          label: 'Utilisateurs',
          icon: Users,
          roles: ['admin', 'director']
        },
        {
          path: '/sites',
          label: 'Sites',
          icon: Building2,
          roles: ['admin', 'director']
        },
        {
          path: '/services',
          label: 'Services',
          icon: Briefcase,
          roles: ['admin', 'director']
        },
        {
          path: '/templates',
          label: 'Missions',
          icon: FileText,
          roles: ['admin', 'director']
        },
        {
          path: '/incidents',
          label: 'Incidents',
          icon: AlertTriangle,
          roles: ['admin', 'director']
        },
        {
          path: '/analytics',
          label: 'Statistiques',
          icon: BarChart3,
          roles: ['admin', 'director']
        }
      ];
    case 'manager':
      return [
        {
          path: '/',
          label: 'Accueil',
          icon: Home,
          roles: ['manager']
        },
        {
          path: '/team',
          label: 'Équipe',
          icon: CalendarDays,
          roles: ['manager']
        },
        {
          path: '/incidents',
          label: 'Incidents',
          icon: AlertTriangle,
          roles: ['manager']
        },
        {
          path: '/analytics',
          label: 'Statistiques',
          icon: BarChart3,
          roles: ['manager']
        }
      ];
    case 'operator':
      return [
        {
          path: '/',
          label: 'Accueil',
          icon: Home,
          roles: ['operator']
        },
        {
          path: '/incidents',
          label: 'Incidents',
          icon: AlertTriangle,
          roles: ['operator']
        }
      ];
    default:
      return [];
  }
};
